import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { TInput, Sel, Toggle, Btn } from "./ui";
import { isValidStreamUrl } from "../../lib/streaming";

const PRESETS = ["Spotify", "Apple Music", "YouTube Music", "TIDAL", "Amazon Music", "SoundCloud", "Bandcamp", "Deezer"];
const norm = (s) => ({ platform: "", url: "", label: null, enabled: true, ...s });

export default function StreamingLinksEditor({ value, onChange }) {
  const links = Array.isArray(value) ? value : [];
  const setAt = (i, patch) => onChange(links.map((s, j) => (j === i ? { ...norm(s), ...patch } : s)));
  const remove = (i) => onChange(links.filter((_, j) => j !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= links.length) return;
    const next = [...links];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = () => onChange([...links, { platform: "Spotify", url: "", label: null, enabled: true }]);

  return (
    <div className="md:col-span-3 border-t border-white/10 pt-4" data-testid="streaming-links-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="font-mono text-[9px] tracking-[0.3em] text-gold">
          STREAMING LINKS — ONLY ENABLED LINKS APPEAR PUBLICLY, IN THIS ORDER
        </p>
        <Btn variant="ghost" onClick={add} testid="stream-link-add">
          <span className="inline-flex items-center gap-2"><Plus size={12} /> ADD STREAMING LINK</span>
        </Btn>
      </div>
      {links.length === 0 && (
        <p className="font-mono text-[9px] tracking-[0.2em] text-white/30 border border-dashed border-white/15 p-4">
          NO STREAMING LINKS YET — NOTHING WILL BE SHOWN PUBLICLY.
        </p>
      )}
      <div className="space-y-2">
        {links.map((raw, i) => {
          const s = norm(raw);
          const preset = PRESETS.includes(s.platform) ? s.platform : "OTHER";
          const invalid = s.enabled && !isValidStreamUrl(s.url);
          return (
            <div key={i} data-testid={`stream-link-row-${i}`}
              className={`border p-3 grid grid-cols-2 md:grid-cols-12 gap-3 items-end ${
                invalid ? "border-red-500/40 bg-red-500/5" : "border-white/10 bg-[#0c0c0c]"}`}>
              <div className="md:col-span-2">
                <Sel label="PLATFORM" value={preset}
                  onChange={(v) => setAt(i, { platform: v === "OTHER" ? "" : v })}
                  options={[...PRESETS.map((p) => ({ v: p, l: p.toUpperCase() })), { v: "OTHER", l: "OTHER…" }]}
                  testid={`stream-link-${i}-platform`} />
              </div>
              {preset === "OTHER" && (
                <div className="md:col-span-2">
                  <TInput label="CUSTOM PLATFORM NAME" value={s.platform}
                    onChange={(v) => setAt(i, { platform: v })} testid={`stream-link-${i}-custom`} />
                </div>
              )}
              <div className={preset === "OTHER" ? "md:col-span-3" : "md:col-span-5"}>
                <TInput label="URL (MUST BE HTTPS://…)" value={s.url} onChange={(v) => setAt(i, { url: v })}
                  placeholder="https://open.spotify.com/album/…" testid={`stream-link-${i}-url`} />
              </div>
              <div className="md:col-span-2">
                <TInput label="DISPLAY LABEL (OPTIONAL)" value={s.label}
                  onChange={(v) => setAt(i, { label: v || null })} testid={`stream-link-${i}-label`} />
              </div>
              <div className="md:col-span-3 flex items-center gap-2 pb-1">
                <Toggle label={s.enabled ? "ENABLED" : "DISABLED"} checked={s.enabled}
                  onChange={(v) => setAt(i, { enabled: v })} testid={`stream-link-${i}-enabled`} />
                <button onClick={() => move(i, -1)} aria-label="Move link up" data-testid={`stream-link-${i}-up`}
                  className="text-white/40 hover:text-white px-1"><ArrowUp size={13} /></button>
                <button onClick={() => move(i, 1)} aria-label="Move link down" data-testid={`stream-link-${i}-down`}
                  className="text-white/40 hover:text-white px-1"><ArrowDown size={13} /></button>
                <button onClick={() => remove(i)} aria-label="Remove link" data-testid={`stream-link-${i}-remove`}
                  className="text-red-400/70 hover:text-red-400 px-1"><Trash2 size={13} /></button>
              </div>
              {invalid && (
                <p className="col-span-2 md:col-span-12 font-mono text-[8px] tracking-[0.2em] text-red-400"
                  data-testid={`stream-link-${i}-invalid`}>
                  ENABLED LINKS NEED A VALID HTTPS:// URL BEFORE SAVING — OR DISABLE THIS ROW.
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
